import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { PersonalOfficeDoorStatus } from "@/lib/enums";
import type { OfficeTheme } from "@/lib/office/office-theme";
import { personalOfficeKeys } from "@/lib/query";

export type PersonalOffice = {
  id: string;
  slotKey: string;
  displayName: string;
  ownerUserId: string | null;
  ownerName: string | null;
  ownerEmail: string | null;
  doorStatus: PersonalOfficeDoorStatus;
  decorIds: string[];
  wallArtFileId: string | null;
  wallArtPlaceholder: string | null;
  theme: OfficeTheme;
  createdAt: string;
  updatedAt: string;
};

async function parseError(res: Response): Promise<string> {
  try {
    const body: unknown = await res.json();
    if (body && typeof body === "object" && "error" in body) {
      const err = (body as { error: unknown }).error;
      if (typeof err === "string") return err;
    }
  } catch {
    // ignore
  }
  return res.statusText || "Request failed";
}

export function usePersonalOffices() {
  return useQuery({
    queryKey: personalOfficeKeys.list(),
    queryFn: async (): Promise<PersonalOffice[]> => {
      const res = await fetch("/api/personal-offices", { credentials: "include" });
      if (!res.ok) throw new Error(await parseError(res));
      const body: unknown = await res.json();
      if (!body || typeof body !== "object" || !("data" in body)) return [];
      const data = (body as { data: unknown }).data;
      return Array.isArray(data) ? (data as PersonalOffice[]) : [];
    },
  });
}

export function useMyPersonalOffice() {
  return useQuery({
    queryKey: personalOfficeKeys.me(),
    queryFn: async (): Promise<PersonalOffice | null> => {
      const res = await fetch("/api/personal-offices/me", { credentials: "include" });
      if (!res.ok) throw new Error(await parseError(res));
      const body: unknown = await res.json();
      if (!body || typeof body !== "object" || !("data" in body)) return null;
      const data = (body as { data: unknown }).data;
      return data && typeof data === "object" ? (data as PersonalOffice) : null;
    },
  });
}

export function useAssignPersonalOffice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; ownerUserId: string | null }) => {
      const res = await fetch(`/api/admin/personal-offices/${input.id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ownerUserId: input.ownerUserId }),
      });
      if (!res.ok) throw new Error(await parseError(res));
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personalOfficeKeys.all });
      toast.success("Office assignment updated");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to assign office");
    },
  });
}

export function useUpdatePersonalOffice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      displayName?: string;
      doorStatus?: PersonalOfficeDoorStatus;
      decorIds?: string[];
      wallArtFileId?: string | null;
      wallArtPlaceholder?: string | null;
      theme?: Partial<OfficeTheme>;
    }) => {
      const { id, ...patch } = input;
      const res = await fetch(`/api/personal-offices/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(await parseError(res));
      return res.json();
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: personalOfficeKeys.all });
      // Quiet for autosave; only surface intentional door toggles.
      if (
        vars.doorStatus !== undefined &&
        Object.keys(vars).filter((k) => k !== "id").length === 1
      ) {
        toast.success(vars.doorStatus === "open" ? "Office open to guests" : "Office closed");
      }
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to update office");
    },
  });
}

export function useClaimPersonalOffice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<PersonalOffice> => {
      const res = await fetch("/api/personal-offices/claim", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error(await parseError(res));
      const body: unknown = await res.json();
      if (!body || typeof body !== "object" || !("data" in body)) {
        throw new Error("Invalid claim response");
      }
      return (body as { data: PersonalOffice }).data;
    },
    onSuccess: (office) => {
      queryClient.invalidateQueries({ queryKey: personalOfficeKeys.all });
      toast.success(`Desk claimed · ${office.displayName}`, {
        description: "Paint the walls, hang a photo, drop some decor — then Step into HQ.",
        duration: 4500,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Could not claim an office");
    },
  });
}
